/**
 * DOCUMENTATION:
 * Component: DesignPatternsProxy
 * Purpose: Educational page teaching the Proxy and Facade design patterns.
 * * * Note for Junior Developers:
 * - Proxy: "The Bouncer." Use this to intercept calls to an object. Great for
 * lazy loading, access control, or caching expensive API results.
 * - Facade: "The Remote Control." Use this to provide a simple function that
 * triggers 10 complex steps under the hood. It protects your UI from needing
 * to know how the "sausage is made."
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Fab, Zoom, Tooltip, Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1508921234172-b68ed335b3e6";

/* ✅ Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

/* ✅ Main Component */
export default function DesignPatternsProxy() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const patterns = useMemo(() => [
        {
            name: "Proxy",
            intent: "Provide a surrogate or placeholder for another object to control access to it.",
            useCase: "Caching API responses, virtual proxies for lazy-loading heavy images, or protection proxies for authorization."
        },
        {
            name: "Facade",
            intent: "Provide a unified interface to a set of interfaces in a subsystem. It defines a higher-level interface that makes the subsystem easier to use.",
            useCase: "A 'Checkout' button that handles inventory checks, payment processing, and email confirmation in one go."
        }
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. The Proxy Pattern (Caching Layer)",
            code: `// ✅ The Original API (Expensive to call)\nclass WeatherAPI {\n  fetch(city) {\n    console.log(\`Fetching real data for \${city}...\`);\n    return "25°C"; \n  }\n}\n\n// ✅ The Proxy (Controls access and caches results)\nclass WeatherProxy {\n  constructor() {\n    this.api = new WeatherAPI();\n    this.cache = {};\n  }\n\n  fetch(city) {\n    if (this.cache[city]) {\n      console.log(\`Returning cached data for \${city}\`);\n      return this.cache[city];\n    }\n    const data = this.api.fetch(city);\n    this.cache[city] = data;\n    return data;\n  }\n}\n\nconst proxy = new WeatherProxy();\nproxy.fetch("Rauma"); // Real fetch\nproxy.fetch("Rauma"); // Hits cache!`,
            desc: "The Proxy pattern is essential for performance. Instead of hitting your database or a paid API every single time, you use a Proxy to intercept the request and return a cached version if it's available."
        },
        {
            title: "2. The Facade Pattern (Complex Subsystems)",
            code: `// ✅ Imagine a messy group of classes\nclass Payment { process(amt) { /*...*/ } }\nclass Inventory { update(id) { /*...*/ } }\nclass Email { sendReceipt(user) { /*...*/ } }\n\n// ✅ The Facade (One simple entry point)\nclass OrderFacade {\n  constructor() {\n    this.payment = new Payment();\n    this.inventory = new Inventory();\n    this.email = new Email();\n  }\n\n  placeOrder(user, item, amount) {\n    this.payment.process(amount);\n    this.inventory.update(item.id);\n    this.email.sendReceipt(user);\n    console.log("Order complete! That was easy.");\n  }\n}\n\n// Usage in UI:\nconst checkout = new OrderFacade();\ncheckout.placeOrder(currentUser, product, 99.00);`,
            desc: "Without a Facade, your React component would have to import and manage three different classes. With a Facade, the UI only cares about 'placeOrder()'. This is the 'Don't Make Me Think' principle applied to code."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    PROXY & FACADE
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Simplification & Control
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Structural patterns aren't just about how objects look; they're about how they behave in a hierarchy. The <strong>Proxy</strong> protects and manages access to its subject, while the <strong>Facade</strong> acts as a friendly diplomat for a complicated country of code.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>The Gatekeepers</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            {patterns.map((item, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", height: "100%", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                                        <Typography variant="subtitle1" sx={{ color: "goldenrod", fontWeight: 800 }}>{item.name}</Typography>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>{item.intent}</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>USE CASE: {item.useCase}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>



                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/design-patterns-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ backgroundColor: "goldenrod", color: "#100720", fontWeight: 800, px: 4, borderRadius: "999px", "&:hover": { backgroundColor: "#e0b200" } }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>

            <Zoom in={true}>
                <Fab onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} size="small" sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod" }}>
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
